import {
  SIGN_IN_CODE_LENGTH,
  formatSignInCode,
  normalizeSignInCode,
  parseCheckoutNext,
  type SignInNext,
} from '@lyrmo/shared';
import { useId, useState } from 'react';
import { Button } from '../../components/Button';
import { useI18n } from '../../i18n/i18n-context';
import { rich } from '../../i18n/rich';
import { useAccount } from './account-context';

interface SignInFormProps {
  readonly intro: string;
  /** Where to continue after signing in. */
  readonly next?: SignInNext;
  readonly onBack?: () => void;
}

/**
 * Passwordless sign-in: asks for an email address and sends a single-use link, with a code for
 * when the email is read on another device, such as a phone.
 */
export function SignInForm({ intro, next, onBack }: SignInFormProps) {
  const { t, describeError } = useI18n();
  const { client, refresh, notify, checkout } = useAccount();
  const inputId = useId();
  const codeId = useId();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'verifying'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setStatus('sending');
    setError(null);
    try {
      await client.requestSignInLink(email.trim(), next);
      setStatus('sent');
    } catch (caught) {
      setError(describeError(caught));
      setStatus('idle');
    }
  }

  async function verify() {
    setStatus('verifying');
    setError(null);
    try {
      await client.signInWithCode(email.trim(), code);
    } catch (caught) {
      setError(describeError(caught));
      setStatus('sent');
      return;
    }
    // The same notices as arriving from the link. Once the account is signed in, the dialog
    // closes by itself.
    const choice = parseCheckoutNext(next);
    notify({
      tone: 'success',
      text: choice ? t.signIn.signedInCheckout : t.signIn.signedIn,
    });
    await refresh();
    if (choice) {
      await checkout(choice).catch((caught: unknown) => {
        notify({ tone: 'warning', text: describeError(caught) });
      });
    }
  }

  if (status === 'sent' || status === 'verifying') {
    return (
      // Its own key, so it mounts afresh rather than reusing the email form's fields.
      <form
        key="code"
        className="sign-in-sent"
        onSubmit={(event) => {
          event.preventDefault();
          void verify();
        }}
      >
        <p className="dialog-intro" role="status">
          {rich(t.signIn.sent, {
            title: <strong>{t.signIn.checkInbox}</strong>,
            email: email.trim(),
          })}
        </p>
        <label className="field-label" htmlFor={codeId}>
          {t.signIn.code}
        </label>
        <input
          id={codeId}
          className="text-input sign-in-code"
          type="text"
          autoComplete="one-time-code"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          required
          placeholder={formatSignInCode('X'.repeat(SIGN_IN_CODE_LENGTH))}
          // Focus moves here from the email form, which is gone.
          autoFocus
          value={formatSignInCode(code)}
          onChange={(event) => {
            // Shown as K7M-4QX, whatever the case; characters codes never use are dropped.
            setCode(normalizeSignInCode(event.target.value).slice(0, SIGN_IN_CODE_LENGTH));
          }}
        />
        {error && (
          <p className="dialog-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <Button
            onClick={() => {
              setStatus('idle');
              setCode('');
              setError(null);
            }}
          >
            {t.signIn.differentEmail}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={status === 'verifying' || code.length !== SIGN_IN_CODE_LENGTH}
          >
            {status === 'verifying' ? t.common.signingIn : t.common.signIn}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      key="email"
      className="sign-in-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <p className="dialog-intro">{intro}</p>
      <label className="field-label" htmlFor={inputId}>
        {t.signIn.email}
      </label>
      <input
        id={inputId}
        className="text-input"
        type="email"
        required
        maxLength={254}
        autoComplete="email"
        placeholder={t.signIn.emailPlaceholder}
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
      />
      {error && (
        <p className="dialog-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        {onBack && <Button onClick={onBack}>{t.common.back}</Button>}
        <Button type="submit" variant="primary" disabled={status === 'sending'}>
          {status === 'sending' ? t.signIn.sending : t.signIn.continue}
        </Button>
      </div>
      <p className="dialog-footnote">
        {rich(t.signIn.agree, {
          // A new tab, so the dialog and the draft stay as they are.
          policy: (
            <a href="/privacy.html" target="_blank" rel="noopener">
              {t.signIn.policy}
            </a>
          ),
        })}
      </p>
    </form>
  );
}
